import background from "@/assets/images/stickers/background.optimized.png";
import cat from "@/assets/images/stickers/cats/cat3.png";
import dog from "@/assets/images/stickers/dogs/dog3.png";
import { createSceneTransform } from "@/features/petHeader/geometry";
import { constrainSticker } from "@/features/petHeader/stickerEditing";
import type {
  AvailableSticker,
  PetHeaderComposition,
  PetHeaderSticker,
  SceneSize,
} from "@/features/petHeader/types";

// Existing meadow artwork and catalog stickers; this is a local demo,
// not a pet/owner draft and not connected to the editor's persistence/API.
export const windowDemo: PetHeaderComposition = {
  petId: "homepage-demo",
  isOwner: false,
  revision: 0,
  header: {
    key: "meadow",
    name: "Солнечная поляна",
    canvasWidth: 2560,
    canvasHeight: 1440,
    layers: [
      { key: "background", kind: "background", order: 0, imageUrl: background },
    ],
    depthSlots: [{ key: "depth-0", order: 0, label: "Передний план" }],
  },
  desktopCrop: { centerX: 0.5, centerY: 0.5, zoom: 1 },
  mobileCrop: { centerX: 0.5, centerY: 0.5, zoom: 1 },
  stickers: [],
  fireflies: [],
  fireflyState: {
    activeCount: 0,
    nextDecayAt: null,
    caughtCount: 0,
    flyingGroups: [],
  },
};

export const demoCatalog: AvailableSticker[] = [
  {
    asset: {
      key: "legacy-cat-3",
      name: "Котик",
      category: "header_specific",
      imageUrl: cat,
      imageWidth: 508,
      imageHeight: 201,
      isActive: true,
    },
    defaultX: 0.4,
    defaultY: 0.64,
    defaultWidth: 0.12,
    defaultDepthSlot: "depth-0",
    isRecommended: true,
  },
  {
    asset: {
      key: "legacy-dog-3",
      name: "Собачка",
      category: "header_specific",
      imageUrl: dog,
      imageWidth: 488,
      imageHeight: 311,
      isActive: true,
    },
    defaultX: 0.54,
    defaultY: 0.6,
    defaultWidth: 0.1,
    defaultDepthSlot: "depth-0",
    isRecommended: true,
  },
];

export interface DemoGeometry {
  viewport: SceneSize;
  top: number;
  bottom: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

// The real editor permits overflow. In this introductory demo keep the whole
// sticker and its size handle visible, clear of the title and controls.
export function containDemoSticker(
  sticker: PetHeaderSticker,
  geometry: DemoGeometry
): PetHeaderSticker {
  const preset = demoCatalog.find((item) => item.asset.key === sticker.assetId);
  if (!preset) return sticker;
  const image = {
    width: windowDemo.header.canvasWidth,
    height: windowDemo.header.canvasHeight,
  };
  const safe = constrainSticker(sticker, preset.defaultWidth, image);
  const transform = createSceneTransform(
    windowDemo.desktopCrop,
    image,
    geometry.viewport
  );
  const scaledWidth = image.width * transform.scale;
  const scaledHeight = image.height * transform.scale;
  const aspect = sticker.asset.imageHeight / sticker.asset.imageWidth;
  const maxPixels = Math.max(
    1,
    Math.min(
      geometry.viewport.width - 32,
      (geometry.bottom - geometry.top) / aspect
    )
  );
  const minPixels = Math.min(80, maxPixels);
  const width = clamp(
    safe.width,
    minPixels / scaledWidth,
    maxPixels / scaledWidth
  );
  const pixels = width * scaledWidth;
  return {
    ...safe,
    width,
    x: clamp(
      safe.x,
      (16 - transform.offsetX) / scaledWidth,
      (geometry.viewport.width - 16 - transform.offsetX - pixels) / scaledWidth
    ),
    y: clamp(
      safe.y,
      (geometry.top - transform.offsetY) / scaledHeight,
      (geometry.bottom - transform.offsetY - pixels * aspect) / scaledHeight
    ),
  };
}
